import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, MessageCircle, Users, Sparkles, Heart, Trophy, ArrowLeft, Check, X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow } from "date-fns";

const notificationSound = new Audio('/sounds/notification.mp3');

interface MatchRequest {
  id: string;
  requester_id: string;
  created_at: string;
  requester: {
    name: string;
    avatar_url: string | null;
  };
}

interface UnreadMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  sender: {
    name: string;
    avatar_url: string | null;
  };
}

interface ActivityItem {
  id: string;
  type: 'impression' | 'rating' | 'session_complete';
  created_at: string;
  data: any;
}

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("matches");
  const [matchRequests, setMatchRequests] = useState<MatchRequest[]>([]);
  const [unreadMessages, setUnreadMessages] = useState<UnreadMessage[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [conversationIds, setConversationIds] = useState<string[]>([]);
  const userPostIdsRef = useRef<string[]>([]);

  // Fetch notifications data
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Fetch pending match requests
      const { data: matches, error: matchesError } = await supabase
        .from('matches')
        .select(`
          id,
          requester_id,
          created_at,
          requester:profiles!matches_requester_id_fkey(name, avatar_url)
        `)
        .eq('receiver_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (matchesError) throw matchesError;

      // Fetch conversations for message subscriptions
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id')
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

      const convIds = conversations?.map(c => c.id) || [];
      setConversationIds(convIds);

      if (convIds.length > 0) {
        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select(`
            id,
            conversation_id,
            sender_id,
            content,
            created_at
          `)
          .in('conversation_id', convIds)
          .neq('sender_id', user.id)
          .is('read_at', null)
          .order('created_at', { ascending: false })
          .limit(20);

        if (messagesError) throw messagesError;

        if (messages && messages.length > 0) {
          const senderIds = [...new Set(messages.map(m => m.sender_id))];
          const { data: senders } = await supabase
            .from('profiles')
            .select('id, name, avatar_url')
            .in('id', senderIds);

          const messagesWithSenders = messages.map(msg => ({
            ...msg,
            sender: senders?.find(s => s.id === msg.sender_id) || { name: 'Unknown', avatar_url: null }
          }));

          setUnreadMessages(messagesWithSenders);
        } else {
          setUnreadMessages([]);
        }
      }

      // Fetch user's showcase posts for activity tracking
      const { data: userPosts } = await supabase
        .from('showcase_posts')
        .select('id')
        .eq('user_id', user.id);
      
      const postIds = userPosts?.map(p => p.id) || [];
      userPostIdsRef.current = postIds;

      // Fetch recent activity (impressions on user's showcase posts)
      const activityItems: ActivityItem[] = [];
      
      if (postIds.length > 0) {
        const { data: impressions } = await supabase
          .from('showcase_impressions')
          .select(`id, created_at, post_id, user_id`)
          .in('post_id', postIds)
          .order('created_at', { ascending: false })
          .limit(10);

        if (impressions && impressions.length > 0) {
          const impressionUserIds = [...new Set(impressions.map(i => i.user_id))];
          const { data: impressionUsers } = await supabase
            .from('profiles')
            .select('id, name, avatar_url')
            .in('id', impressionUserIds);

          impressions.forEach(imp => {
            const impUser = impressionUsers?.find(u => u.id === imp.user_id);
            activityItems.push({
              id: imp.id,
              type: 'impression',
              created_at: imp.created_at,
              data: {
                user_name: impUser?.name || 'Someone',
                user_avatar: impUser?.avatar_url
              }
            });
          });
        }
      }

      // Fetch recent ratings received
      const { data: ratings } = await supabase
        .from('ratings')
        .select(`id, created_at, rating, rater_id`)
        .eq('rated_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (ratings && ratings.length > 0) {
        const raterIds = [...new Set(ratings.map(r => r.rater_id))];
        const { data: raters } = await supabase
          .from('profiles')
          .select('id, name, avatar_url')
          .in('id', raterIds);

        ratings.forEach(rating => {
          const rater = raters?.find(r => r.id === rating.rater_id);
          activityItems.push({
            id: rating.id,
            type: 'rating',
            created_at: rating.created_at,
            data: {
              user_name: rater?.name || 'Someone',
              user_avatar: rater?.avatar_url,
              rating: rating.rating
            }
          });
        });
      }

      activityItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setActivities(activityItems.slice(0, 20));

      setMatchRequests(matches?.map(m => ({
        ...m,
        requester: m.requester as { name: string; avatar_url: string | null }
      })) || []);

    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, fetchNotifications]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user) return;

    // Subscribe to new match requests
    const matchChannel = supabase
      .channel('notifications-matches')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'matches',
          filter: `receiver_id=eq.${user.id}`
        },
        async (payload) => {
          // Fetch requester profile
          const { data: requester } = await supabase
            .from('profiles')
            .select('name, avatar_url')
            .eq('id', payload.new.requester_id)
            .maybeSingle();

          const newMatch: MatchRequest = {
            id: payload.new.id,
            requester_id: payload.new.requester_id,
            created_at: payload.new.created_at,
            requester: requester || { name: 'Unknown', avatar_url: null }
          };

          setMatchRequests(prev => [newMatch, ...prev]);
          notificationSound.play().catch(() => {});
          
          toast({
            title: "New match request!",
            description: `${requester?.name || 'Someone'} wants to connect with you.`,
          });
        }
      )
      .subscribe();

    // Subscribe to new messages
    const messageChannel = supabase
      .channel('notifications-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          // Only process if user is in the conversation and not the sender
          if (payload.new.sender_id === user.id) return;
          
          // Check if this conversation belongs to the user
          const { data: conv } = await supabase
            .from('conversations')
            .select('id')
            .eq('id', payload.new.conversation_id)
            .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
            .maybeSingle();
          
          if (!conv) return;

          // Fetch sender profile
          const { data: sender } = await supabase
            .from('profiles')
            .select('name, avatar_url')
            .eq('id', payload.new.sender_id)
            .maybeSingle();

          const newMessage: UnreadMessage = {
            id: payload.new.id,
            conversation_id: payload.new.conversation_id,
            sender_id: payload.new.sender_id,
            content: payload.new.content,
            created_at: payload.new.created_at,
            sender: sender || { name: 'Unknown', avatar_url: null }
          };

          setUnreadMessages(prev => [newMessage, ...prev.filter(m => m.id !== newMessage.id)]);
          notificationSound.play().catch(() => {});
        }
      )
      .subscribe();

    // Subscribe to message reads (to remove from unread list)
    const messageReadChannel = supabase
      .channel('notifications-message-reads')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          if (payload.new.read_at && !payload.old.read_at) {
            setUnreadMessages(prev => prev.filter(m => m.id !== payload.new.id));
          }
        }
      )
      .subscribe();

    // Subscribe to new impressions on user's posts
    const impressionChannel = supabase
      .channel('notifications-impressions')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'showcase_impressions'
        },
        async (payload) => {
          // Check if this is for user's post
          if (!userPostIdsRef.current.includes(payload.new.post_id)) return;

          const { data: impUser } = await supabase
            .from('profiles')
            .select('name, avatar_url')
            .eq('id', payload.new.user_id)
            .maybeSingle();

          const newActivity: ActivityItem = {
            id: payload.new.id,
            type: 'impression',
            created_at: payload.new.created_at,
            data: {
              user_name: impUser?.name || 'Someone',
              user_avatar: impUser?.avatar_url
            }
          };

          setActivities(prev => [newActivity, ...prev].slice(0, 20));
        }
      )
      .subscribe();

    // Subscribe to new ratings
    const ratingChannel = supabase
      .channel('notifications-ratings')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ratings',
          filter: `rated_user_id=eq.${user.id}`
        },
        async (payload) => {
          const { data: rater } = await supabase
            .from('profiles')
            .select('name, avatar_url')
            .eq('id', payload.new.rater_id)
            .maybeSingle();

          const newActivity: ActivityItem = {
            id: payload.new.id,
            type: 'rating',
            created_at: payload.new.created_at,
            data: {
              user_name: rater?.name || 'Someone',
              user_avatar: rater?.avatar_url,
              rating: payload.new.rating
            }
          };

          setActivities(prev => [newActivity, ...prev].slice(0, 20));
          notificationSound.play().catch(() => {});
          
          toast({
            title: "New rating!",
            description: `${rater?.name || 'Someone'} rated you ${payload.new.rating} stars.`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(matchChannel);
      supabase.removeChannel(messageChannel);
      supabase.removeChannel(messageReadChannel);
      supabase.removeChannel(impressionChannel);
      supabase.removeChannel(ratingChannel);
    };
  }, [user, toast]);

  const handleAcceptMatch = async (matchId: string, requesterId: string) => {
    try {
      const { error } = await supabase
        .from('matches')
        .update({ status: 'accepted' })
        .eq('id', matchId);

      if (error) throw error;

      toast({
        title: "Match accepted!",
        description: "You can now chat with your new match.",
      });

      // Remove from list
      setMatchRequests(prev => prev.filter(m => m.id !== matchId));

      // Navigate to chat
      navigate(`/chat?userId=${requesterId}`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to accept match request.",
        variant: "destructive",
      });
    }
  };

  const handleRejectMatch = async (matchId: string) => {
    try {
      const { error } = await supabase
        .from('matches')
        .update({ status: 'rejected' })
        .eq('id', matchId);

      if (error) throw error;

      toast({
        title: "Match declined",
        description: "The match request has been declined.",
      });

      setMatchRequests(prev => prev.filter(m => m.id !== matchId));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to decline match request.",
        variant: "destructive",
      });
    }
  };

  const totalNotifications = matchRequests.length + unreadMessages.length + activities.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="gradient-primary text-white p-4 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
              </h1>
              {totalNotifications > 0 && (
                <p className="text-white/90 text-xs">
                  {totalNotifications} new notification{totalNotifications !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-md mx-auto px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="matches" className="flex items-center gap-1 text-xs">
              <Users className="h-4 w-4" />
              Matches
              {matchRequests.length > 0 && (
                <Badge variant="destructive" className="h-5 min-w-5 px-1 text-xs">
                  {matchRequests.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-1 text-xs">
              <MessageCircle className="h-4 w-4" />
              Messages
              {unreadMessages.length > 0 && (
                <Badge variant="destructive" className="h-5 min-w-5 px-1 text-xs">
                  {unreadMessages.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-1 text-xs">
              <Sparkles className="h-4 w-4" />
              Activity
              {activities.length > 0 && (
                <Badge variant="secondary" className="h-5 min-w-5 px-1 text-xs">
                  {activities.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Match Requests Tab */}
          <TabsContent value="matches" className="mt-4 space-y-3">
            {matchRequests.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <h4 className="font-semibold mb-1">No pending requests</h4>
                <p className="text-sm text-muted-foreground">
                  When someone wants to match with you, you'll see it here.
                </p>
              </Card>
            ) : (
              matchRequests.map((request) => (
                <Card key={request.id} className="p-4 shadow-soft">
                  <div className="flex items-center gap-3">
                    <Avatar 
                      className="h-12 w-12 cursor-pointer"
                      onClick={() => navigate(`/profile?id=${request.requester_id}`)}
                    >
                      <AvatarImage 
                        src={request.requester.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${request.requester_id}`} 
                      />
                      <AvatarFallback>
                        {request.requester.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{request.requester.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Wants to connect • {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="icon" 
                        variant="outline"
                        className="h-9 w-9 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => handleRejectMatch(request.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => handleAcceptMatch(request.id, request.requester_id)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-4 space-y-3">
            {unreadMessages.length === 0 ? (
              <Card className="p-8 text-center">
                <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <h4 className="font-semibold mb-1">All caught up!</h4>
                <p className="text-sm text-muted-foreground">
                  No unread messages at the moment.
                </p>
              </Card>
            ) : (
              unreadMessages.map((message) => (
                <Card 
                  key={message.id} 
                  className="p-4 shadow-soft cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => navigate(`/chat?conversationId=${message.conversation_id}`)}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage 
                        src={message.sender.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${message.sender_id}`} 
                      />
                      <AvatarFallback>
                        {message.sender.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{message.sender.name}</p>
                      <p className="text-sm text-muted-foreground line-clamp-1">{message.content}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <Badge variant="destructive" className="h-2 w-2 p-0 rounded-full" />
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-4 space-y-3">
            {activities.length === 0 ? (
              <Card className="p-8 text-center">
                <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <h4 className="font-semibold mb-1">No recent activity</h4>
                <p className="text-sm text-muted-foreground">
                  Activity from impressions and ratings will appear here.
                </p>
              </Card>
            ) : (
              activities.map((activity) => (
                <Card key={activity.id} className="p-4 shadow-soft">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      activity.type === 'impression' ? 'bg-pink-500/20' : 
                      activity.type === 'rating' ? 'bg-amber-500/20' : 'bg-green-500/20'
                    }`}>
                      {activity.type === 'impression' && <Heart className="h-5 w-5 text-pink-500" />}
                      {activity.type === 'rating' && <Trophy className="h-5 w-5 text-amber-500" />}
                      {activity.type === 'session_complete' && <Check className="h-5 w-5 text-green-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        {activity.type === 'impression' && (
                          <><span className="font-semibold">{activity.data.user_name}</span> was impressed by your showcase</>
                        )}
                        {activity.type === 'rating' && (
                          <><span className="font-semibold">{activity.data.user_name}</span> rated you {activity.data.rating} stars</>
                        )}
                        {activity.type === 'session_complete' && (
                          <>You completed a session!</>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Navigation />
    </div>
  );
};

export default Notifications;